import * as React from "react";

import { cn } from "@/lib/utils";

// Every admin table fits the viewport instead of scrolling sideways. That is
// the default, applied here rather than page by page, so a table nobody has
// revisited can't quietly reintroduce a horizontal scrollbar:
//
//   FIT_CELLS  – 13px type (a compromise: 14px scrolls, 12px is hard to read),
//                and long unbroken tokens (emails, URLs, ids) allowed to break
//                so they stop pinning a column wide; cell padding is tight too,
//                set on the cells below. Width for this comes from not spending
//                it twice: see shortDate() in lib/utils and prefer stacking two
//                related values in one column over two narrow columns.
//   KEEP_WHOLE – the values that must never be split back up. It covers cells
//                marked `tabular-nums` or `text-right`, which is where numbers
//                normally live. A numeric cell that is neither — a left-aligned
//                count or money column — MUST carry `whitespace-nowrap`, or
//                "$8,500,000" can render as "$8,50 / 0,000" in a narrow column.
//
// Measured with worst-case row content against the content width left beside
// the 18rem sidebar: 11–14 column tables (Users, Brand Users) overflow by 0px
// at 944px — a 1280px window — and the 12-column Creator Payments at 1024px.
// Before this it was 474px of overflow at 1392px. Opt a table out with
// WIDE_TABLE_SCROLL.
//
// FIT_CELLS uses `[&_td]` descendant variants (not plain `text-xs` on the
// table) deliberately: cells set their own `text-sm`, which would win over an
// inherited size but loses to a descendant selector. Padding stays on the cells
// themselves so a page's own `pl-6` / `pr-4` still wins through cn().
// The fit above was measured for desktop widths. A phone is a different
// problem: 11–14 columns cannot fit 390px, and at that width
// `overflow-wrap: anywhere` starts breaking mid-token — an email renders as
// "kowa / l8312 / 4@g / wsha / re.co / m". Below `sm` the table therefore keeps
// values whole and scrolls sideways, which is the normal phone pattern for a
// data table; the measured desktop fit is untouched from `sm` up.
//
// Written as `max-sm:` (adds rules below the breakpoint) rather than a
// `sm:…whitespace-normal` reset above it, on purpose: the reset is a descendant
// selector and would out-specify the per-cell `whitespace-nowrap` marks on
// name/date/handle cells, silently undoing them on desktop.
const FIT_CELLS =
  "[&_th]:text-[13px] [&_td]:text-[13px] [&_td]:[overflow-wrap:anywhere] " +
  "max-sm:[&_th]:whitespace-nowrap max-sm:[&_td]:whitespace-nowrap " +
  "max-sm:[&_td]:[overflow-wrap:normal]";
const KEEP_WHOLE =
  "[&_td.tabular-nums]:whitespace-nowrap [&_td.text-right]:whitespace-nowrap";

// containerClassName styles the scroll wrapper, which keeps `overflow-auto`
// purely as a last resort — with fitting cells it produces no scrollbar unless a
// table is physically too wide, which is worth seeing rather than clipping.
// Callers that need a height cap must set it here rather than nesting their own
// overflow div — with two nested scroll containers the horizontal scrollbar ends
// up below the clipped rows, out of reach until you scroll all the way down.
const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement> & { containerClassName?: string }
>(({ className, containerClassName, ...props }, ref) => (
  <div className={cn("relative w-full overflow-auto", containerClassName)}>
    <table
      ref={ref}
      className={cn("w-full caption-bottom text-sm", FIT_CELLS, KEEP_WHOLE, className)}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

/**
 * Restores the old scroll-sideways behaviour (single-line cells, roomy padding,
 * normal type) for one table: pass it as `className` on <Table>. `!` modifiers
 * because it and the fit defaults are both descendant selectors of equal
 * specificity — without them which one wins depends on Tailwind's class order.
 *
 * Used by the Exclusive Campaigns (/hiring) tables, which were deliberately
 * left as they were. Prefer fitting a table to the screen over reaching for
 * this — the rest of the admin no longer scrolls horizontally.
 */
export const WIDE_TABLE_SCROLL =
  "[&_th]:!whitespace-nowrap [&_td]:!whitespace-nowrap [&_th]:!px-4 [&_td]:!px-4 [&_td]:!py-4 " +
  "[&_th]:!text-sm [&_td]:!text-sm [&_td]:![overflow-wrap:normal]";

/**
 * Marks a cell whose value must never be split across lines — a number, a date,
 * a code. Rarely needed by hand: the table already protects `tabular-nums` and
 * `text-right` cells, which is where numbers live. Reach for it when a
 * left-aligned cell holds a value that reads wrong when broken.
 */
export const KEEP_ON_ONE_LINE = "whitespace-nowrap";

const TableHeader = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />,
);
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tbody ref={ref} className={cn("[&_tr:last-child]:border-0", className)} {...props} />
  ),
);
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
  ({ className, ...props }, ref) => (
    <tfoot ref={ref} className={cn("border-t bg-muted/50 font-medium [&>tr]:last:border-b-0", className)} {...props} />
  ),
);
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn("border-b transition-colors data-[state=selected]:bg-muted hover:bg-muted/50", className)}
      {...props}
    />
  ),
);
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<HTMLTableCellElement, React.ThHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={cn(
        // Headers wrap and sit tight so a wide table still fits the viewport —
        // a two-line "Creator / Rate" header costs one row of height and buys
        // width for every row below it. Set on the cell (not as a [&_th]
        // variant on the table) so a per-cell `whitespace-nowrap` actually
        // wins — cn() merges them, whereas a descendant selector would
        // out-specify it. Keep nowrap per-cell for headers that must not break.
        "h-12 px-1.5 text-left align-middle font-medium text-muted-foreground [&:has([role=checkbox])]:pr-0",
        className,
      )}
      {...props}
    />
  ),
);
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<HTMLTableCellElement, React.TdHTMLAttributes<HTMLTableCellElement>>(
  ({ className, ...props }, ref) => (
    // Multi-word values (names, campaigns, product titles) fold onto a second
    // line instead of pushing the table off screen; long unbroken tokens break
    // too, via FIT_CELLS on the table. Numbers and dates are held on one line
    // by KEEP_WHOLE — "$1,326.00" must never render as "$1,326. 00".
    <td
      ref={ref}
      className={cn("px-1.5 py-2.5 align-middle [&:has([role=checkbox])]:pr-0", className)}
      {...props}
    />
  ),
);
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<HTMLTableCaptionElement, React.HTMLAttributes<HTMLTableCaptionElement>>(
  ({ className, ...props }, ref) => (
    <caption ref={ref} className={cn("mt-4 text-sm text-muted-foreground", className)} {...props} />
  ),
);
TableCaption.displayName = "TableCaption";

export { Table, TableHeader, TableBody, TableFooter, TableHead, TableRow, TableCell, TableCaption };
